import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import EntitlementsCombobox from '@/components/app/entitlements-combobox';
import { ENTITLEMENT_OPTIONS } from '@/lib/entitlements';
import type { LicenseRequest } from '@/schemas/license-requests';

const formSchema = z.object({
  serial: z
    .string()
    .min(1, 'errors.validation.serialRequired')
    .max(128)
    .regex(/^[\w-]+$/, 'errors.validation.serialFormat'),
  buyerCompany: z.string().min(1, 'errors.validation.buyerRequired').max(256),
  type: z.enum(['perpetual', 'subscription', 'trial'] as const),
  maxActivations: z.coerce.number().int().min(1).max(1000),
  entitlements: z.array(z.object({ module: z.string(), tier: z.string() })),
  offlineRequested: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the created request after a successful submit. */
  onCreated?: (request: LicenseRequest) => void;
}

export function CreateRequestDialog({ open, onOpenChange, onCreated }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      serial: '',
      buyerCompany: '',
      type: 'perpetual' as const,
      maxActivations: 1,
      entitlements: [] as { module: string; tier: string }[],
      offlineRequested: false,
    },
  });

  React.useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = async (data: FormValues) => {
    const payload = {
      serial: data.serial,
      buyerCompany: data.buyerCompany,
      configuration: {
        type: data.type,
        maxActivations: data.maxActivations,
        entitlements: data.entitlements,
      },
      offlineRequested: data.offlineRequested,
    };
    try {
      const resp = await apiFetch('/license-requests', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const created = (await resp.json()) as LicenseRequest;
      toast.success(t('requests.new.toastCreated', { serial: data.serial }));
      void queryClient.invalidateQueries({ queryKey: ['license-requests'] });
      onOpenChange(false);
      onCreated?.(created);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError('root', { message: t('errors.accessDenied') });
      } else if (err instanceof ApiError && err.status === 409) {
        setError('serial', { message: t('requests.new.conflictSerial') });
      } else if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        try {
          const body = JSON.parse(err.message);
          if (body.errors) {
            for (const [k, v] of Object.entries(body.errors))
              setError(k as keyof FormValues, { message: Array.isArray(v) ? v[0] : String(v) });
          } else if (body.error) setError('root', { message: body.error });
          else setError('root', { message: err.message });
        } catch {
          setError('root', { message: err.message });
        }
      } else {
        setError('root', { message: t('errors.network') });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('requests.new.title')}</DialogTitle>
          <DialogDescription>{t('requests.new.subtitle')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor="req-serial">{t('requests.new.serialLabel')}</Label>
            <Input
              id="req-serial"
              className="font-mono"
              placeholder={t('requests.new.serialPlaceholder')}
              aria-describedby="req-serial-hint"
              {...register('serial')}
            />
            <p id="req-serial-hint" className="text-[12.5px] text-muted-foreground">
              {t('requests.new.serialHint')}
            </p>
            {errors.serial && (
              <p className="text-[13px] text-destructive">{t(errors.serial.message ?? '')}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="req-buyer">{t('requests.new.buyerLabel')}</Label>
            <Input id="req-buyer" {...register('buyerCompany')} />
            {errors.buyerCompany && (
              <p className="text-[13px] text-destructive">{t(errors.buyerCompany.message ?? '')}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="req-type">{t('requests.new.typeLabel')}</Label>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="req-type" aria-label={t('requests.new.typeLabel')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="perpetual">{t('common.type.perpetual')}</SelectItem>
                      <SelectItem value="subscription">{t('common.type.subscription')}</SelectItem>
                      <SelectItem value="trial">{t('common.type.trial')}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="req-max">{t('requests.new.maxActivationsLabel')}</Label>
              <Input
                id="req-max"
                type="number"
                min={1}
                max={1000}
                {...register('maxActivations')}
              />
              {errors.maxActivations && (
                <p className="text-[13px] text-destructive">
                  {t('errors.validation.maxActivations')}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('requests.new.entitlementsLabel')}</Label>
            <Controller
              control={control}
              name="entitlements"
              render={({ field }) => (
                <EntitlementsCombobox
                  options={ENTITLEMENT_OPTIONS}
                  selected={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="flex items-start gap-3 rounded-md border border-border p-3">
            <Controller
              control={control}
              name="offlineRequested"
              render={({ field }) => (
                <Checkbox
                  id="req-offline"
                  checked={field.value}
                  onCheckedChange={(v) => field.onChange(v === true)}
                  aria-describedby="req-offline-hint"
                />
              )}
            />
            <div className="space-y-1">
              <Label htmlFor="req-offline" className="cursor-pointer font-medium">
                {t('requests.new.offlineLabel')}
              </Label>
              <p id="req-offline-hint" className="text-[12.5px] text-muted-foreground">
                {t('requests.new.offlineHint')}
              </p>
            </div>
          </div>

          {errors.root && (
            <p role="alert" className="text-[13px] text-destructive">
              {errors.root.message}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !isValid}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {t('requests.new.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateRequestDialog;
